import { addAttachments, removeAttachments } from '@/services/api/chatService';
import {
  CheckCircleTwoTone,
  CloseCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  FileOutlined,
  LoadingOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Button, Input, message, Modal, Spin, Upload } from 'antd';
import Dragger from 'antd/es/upload/Dragger';
import type { ChangeEvent } from 'react';
import { useRef, useState, useEffect } from 'react';
import styles from '@/styles/ChatWindow.module.scss';
import { useTranslation } from 'react-i18next';

const FILE_LIMIT = 10;

const ModalAddFiles = ({ inquiryId, inquiry, setReloadInquiry, open, onCancel, externalLoading, setExternalLoading }: any) => {
  const { t } = useTranslation();
  const draggerContainerRef = useRef<HTMLDivElement>(null);
  const uploadPendingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFilesRef = useRef<any[]>([]);
  const [fileList, setFileList] = useState<any[]>([]);
  const [tags, setTags] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(externalLoading || false);

  useEffect(() => {
    if (externalLoading !== loading) {
      setLoading(externalLoading);
    }
  }, [externalLoading]);

  const setLoadingState = (value: boolean) => {
    setLoading(value);
    setExternalLoading(value);
  };

  const handleCancel = () => {
    if (loading || externalLoading) {
      message.warning(t('home.chat.attachments.files.uploadInProgressWarning'));
      return;
    }
    onCancel();
  };

  useEffect(() => {
    return () => {
      if (uploadPendingRef.current) {
        clearTimeout(uploadPendingRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (loading || externalLoading) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [loading, externalLoading]);

  const handleUpload = async (filesToUpload: any[]) => {
    if (filesToUpload.length === 0 || filesToUpload.length > FILE_LIMIT) {
      if (filesToUpload.length > FILE_LIMIT) {
        message.error(t('home.chat.attachments.files.limitError', { n: FILE_LIMIT }));
      }
      return;
    }

    const uploadingFiles = filesToUpload.map(f => ({ 
      ...f, 
      status: 'uploading', 
      percent: 50 
    }));
    setFileList(uploadingFiles);

    const formData = new FormData();
    filesToUpload.forEach((file: any) => {
      const fileObj = file.originFileObj || file;
      formData.append('files[]', fileObj);
    });
    
    if (tags) {
      formData.append('tags', tags);
    }

    setLoadingState(true);
    try {
      const response = await addAttachments(inquiryId, formData);
      if (!response.success) {
        message.error(response.error);
        setLoadingState(false);
        setFileList(filesToUpload.map(f => ({ ...f, status: 'error' })));
        return;
      }
      
      setFileList([]);
      setTags('');
      setReloadInquiry((prev: boolean) => !prev);
      setLoadingState(false);
      onCancel();
    } catch (error) {
      setLoadingState(false);
      setFileList(filesToUpload.map(f => ({ ...f, status: 'error' })));
      message.error(t('home.chat.attachments.files.error') || 'Error during upload');
    }
  };

  const handleFileChange = (info: any) => {
    const isNewAddition = info.fileList.length > fileList.length;
    setFileList(info.fileList);
    pendingFilesRef.current = info.fileList;

    if (isNewAddition && !loading) {
      if (uploadPendingRef.current) {
        clearTimeout(uploadPendingRef.current);
      }
      uploadPendingRef.current = setTimeout(() => {
        uploadPendingRef.current = null;
        handleUpload(pendingFilesRef.current);
      }, 0);
    }
  };

  const customIconRender = (file: any) => {
    if (file.status === 'error') return <CloseCircleOutlined style={{ color: 'red' }} />;
    return <FileOutlined style={{ fontSize: '30px' }} />;
  };

  const beforeUpload = (file: any) => {
    const isAllowedType = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
    ].includes(file.type);

    if (!isAllowedType) {
      message.error(t('home.chat.attachments.files.invalidFile', { name: file.name }));
      return Upload.LIST_IGNORE;
    }

    return false; 
  };

  const props = {
    name: 'file',
    multiple: true,
    fileList: fileList,
    beforeUpload: beforeUpload,
    iconRender: customIconRender,
    onRemove(file: any) {
      if (loading) return false;
      setFileList((prevFileList: any) => prevFileList.filter((item: any) => item.uid !== file.uid));
    },
  };

  const handleExternalClick = () => {
    if (loading) return;
    const input = draggerContainerRef.current?.querySelector('input[type="file"]') as HTMLInputElement;
    if (input) input.click();
  };

  const removeFileAttachments = async (file: any) => {
    if (loading) return;
    const { success } = await removeAttachments(inquiryId, [file.id]);
    if (success) setReloadInquiry((prev: boolean) => !prev);
  };

  return (
    <div className="modal-add-attchments">
      <Modal
        title={t('home.chat.attachments.files.title')}
        open={open}
        onCancel={handleCancel}
        closable={true}
        footer={
          (!inquiry?.attachments?.length && !fileList.length && !loading)
            ? null
            : [
                <Button
                  key="upload-more-btn"
                  disabled={loading}
                  onClick={() => handleExternalClick()}
                >
                  {t('home.chat.attachments.files.uploadMore')}
                </Button>,
                <Button
                  key="confirm-upload-btn"
                  type="primary"
                  onClick={fileList.length > 0 ? () => handleUpload(fileList) : onCancel}
                  loading={loading}
                >
                  {t('home.chat.attachments.files.confirmUpload')}
                </Button>,
              ]
        }
        width={'40%'}
      >
        {inquiry?.attachments?.map((file: any, index: number) => (
          <div
            key={file?.id}
            className={styles.wrapAttachments}
            style={{ borderBottom: index === inquiry?.attachments.length - 1 ? 'none' : '1px solid #0000000F' }}
          >
            <div className={styles.attachments}>
              <div className={styles.attachmentName}>
                <FileOutlined style={{ fontSize: '30px' }} />
                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>{file.name}</div>
              </div>
              <DeleteOutlined
                className={styles.deleteIcon}
                onClick={() => removeFileAttachments(file)}
                style={{ cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}
              />
            </div>
          </div>
        ))}
        <div
          style={{ padding: '20px', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', borderTop: '1px solid #0000000F' }}
          ref={draggerContainerRef}
        >
          <Dragger {...props} disabled={loading} onChange={handleFileChange}>
            <div style={{ display: 'flex', height: '100%', gap: 20 }}>
              <UploadOutlined style={{ fontSize: '40px', color: '#00000073' }} />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <p>{t('home.chat.attachments.files.dragNdrop')} <a>{t('home.chat.attachments.files.chooseFile')}</a></p>
                <br />
                <p>・{t('home.chat.attachments.files.rule1', { n: FILE_LIMIT })}</p>
                <p>・{t('home.chat.attachments.files.rule2')}</p>
              </div>
            </div>
          </Dragger>
        </div>
      </Modal>
    </div>
  );
};

export default ModalAddFiles;
